import type { CalendarMeeting } from '@ai-meeting/shared'
import { suggestProjectStrict, type Project } from '@/services/meetingBrief'

/**
 * Which calendar rows should offer Brief, kept out of the table.
 *
 * The listing paginates and filters a copy of the rows; this rule has to see
 * the whole provider week and a clock the caller owns, or search / a later
 * page would move Brief onto the wrong meeting.
 */

/** Matches the backend calendar read window. */
export const BRIEF_WINDOW_DAYS = 7

const MS_PER_DAY = 24 * 60 * 60 * 1000

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** `YYYY-MM-DD` in the reader's local calendar, not the UTC date. */
export function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function parseInstant(value: string): Date | null {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function isActive(item: CalendarMeeting, now: Date): boolean {
  const endsAt = parseInstant(item.endsAt)
  return endsAt !== null && endsAt.getTime() > now.getTime()
}

function startsAtMs(item: CalendarMeeting): number | null {
  const startsAt = parseInstant(item.startsAt)
  return startsAt?.getTime() ?? null
}

/** Still running and starting within the next seven local days. */
export function isActiveInBriefWindow(item: CalendarMeeting, now: Date): boolean {
  if (!isActive(item, now)) return false
  const startMs = startsAtMs(item)
  if (startMs === null) return false
  return startMs <= now.getTime() + BRIEF_WINDOW_DAYS * MS_PER_DAY
}

export function resolveProjectKeyForBriefRule(
  item: CalendarMeeting,
  storedProjectKeyByEventId: ReadonlyMap<string, string>,
  projects: Project[]
): string | null {
  const stored = storedProjectKeyByEventId.get(item.id)
  if (stored) return stored
  return suggestProjectStrict(item.title, projects)?.projectKey ?? null
}

/**
 * Event ids that should offer Brief: the nearest active meeting in the window
 * per project, plus every unmapped active meeting in the window.
 */
export function buildBriefEligibleMeetingIds(
  items: CalendarMeeting[],
  now: Date,
  storedProjectKeyByEventId: ReadonlyMap<string, string>,
  projects: Project[]
): Set<string> {
  const eligible = new Set<string>()
  const nearestByProject = new Map<string, { id: string; startMs: number }>()

  for (const item of items) {
    if (!isActiveInBriefWindow(item, now)) continue
    const startMs = startsAtMs(item)
    if (startMs === null) continue

    const projectKey = resolveProjectKeyForBriefRule(item, storedProjectKeyByEventId, projects)
    if (!projectKey) {
      eligible.add(item.id)
      continue
    }

    const current = nearestByProject.get(projectKey)
    if (!current || startMs < current.startMs || (startMs === current.startMs && item.id < current.id)) {
      nearestByProject.set(projectKey, { id: item.id, startMs })
    }
  }

  for (const entry of nearestByProject.values()) {
    eligible.add(entry.id)
  }

  return eligible
}

export function isBriefEligible(item: CalendarMeeting, eligibleIds: ReadonlySet<string>): boolean {
  return eligibleIds.has(item.id)
}
